import React, { useState, useEffect, useRef } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { K, inputStyle } from '../constants';
import { Btn } from '../components/ui';
import { useUserProfile, TITLES, SHIFTS, CERTIFICATIONS } from '../hooks/useUserProfile';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Camera, Check } from 'lucide-react';

function Avatar({ photoUrl, name, size = 72 }) {
  if (photoUrl) {
    return <img src={photoUrl} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', border: `1px solid ${K.bdr2}` }} />;
  }
  const letter = (name || '?').charAt(0).toUpperCase();
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: K.red, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.4, fontWeight: 600, color: '#fff', fontFamily: K.fSans, border: `1px solid ${K.bdr}`, flexShrink: 0 }}>
      {letter}
    </div>
  );
}

// Small avatar for header
export function ProfileAvatar({ photoUrl, name, size = 28, onClick }) {
  if (photoUrl) {
    return <button onClick={onClick} style={{ width: size, height: size, borderRadius: '50%', overflow: 'hidden', border: `1.5px solid ${K.bdr2}`, cursor: 'pointer', padding: 0, background: 'none', flexShrink: 0 }}>
      <img src={photoUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
    </button>;
  }
  const letter = (name || '?').charAt(0).toUpperCase();
  return (
    <button onClick={onClick} style={{ width: size, height: size, borderRadius: '50%', background: K.red, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#fff', fontFamily: K.fSans, border: `1px solid ${K.bdr}`, cursor: 'pointer', flexShrink: 0, padding: 0 }}>
      {letter}
    </button>
  );
}

export function ProfileModal({ user, onClose }) {
  const { profile, loading, updateProfile, uploadPhoto } = useUserProfile(user);
  const push = usePushNotifications(user);
  const fileRef = useRef(null);

  const [displayName, setDisplayName] = useState('');
  const [title, setTitle] = useState('');
  const [shift, setShift] = useState('');
  const [phone, setPhone] = useState('');
  const [certs, setCerts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoErr, setPhotoErr] = useState('');
  const [saved, setSaved] = useState(false);

  // Populate form from profile
  useEffect(() => {
    if (!profile) return;
    setDisplayName(profile.displayName || '');
    setTitle(profile.title || '');
    setShift(profile.shift || '');
    setPhone(profile.phone || '');
    setCerts(profile.certifications || []);
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    await updateProfile({
      displayName: displayName.trim(),
      title,
      shift,
      phone: phone.trim(),
      certifications: certs,
    });
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    setPhotoErr('');
    if (!file.type?.startsWith('image/')) {
      setPhotoErr('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setPhotoErr('Image must be under 5 MB.');
      return;
    }
    setUploading(true);
    try {
      await uploadPhoto(file);
    } catch (err) {
      setPhotoErr('Photo upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const toggleCert = (cert) => {
    setCerts((prev) => prev.includes(cert) ? prev.filter((c) => c !== cert) : [...prev, cert]);
  };

  if (loading) return null;

  const roleBadge = {
    admin: { label: 'Admin', col: K.red },
    officer: { label: 'Officer', col: K.gold },
    member: { label: 'Member', col: K.info },
  }[profile?.role || 'member'];

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 400, background: '#000000cc' }} />

      <div className="hc-modal-sheet" style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 401, background: K.bg1, borderRadius: '16px 16px 0 0', padding: 24, maxWidth: 480, margin: '0 auto', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ width: 40, height: 4, background: K.bdr2, borderRadius: 2, margin: '0 auto 20px' }} />

        {/* Header with avatar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <div style={{ position: 'relative' }}>
            <Avatar photoUrl={profile?.photoUrl} name={displayName || profile?.email} />
            <button
              onClick={() => fileRef.current?.click()}
              style={{
                position: 'absolute', bottom: -2, right: -2, width: 26, height: 26, borderRadius: '50%',
                background: K.bg2, border: `1px solid ${K.bdr2}`, cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, color: K.t2,
              }}
            >
              {uploading ? '...' : <Camera size={14} color={K.t2} strokeWidth={1.75} />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={handlePhoto} style={{ display: 'none' }} />
            {photoErr && (
              <div style={{ position: 'absolute', top: '100%', left: 0, whiteSpace: 'nowrap', fontSize: 11, color: K.red, marginTop: 4 }}>{photoErr}</div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: K.fSans, fontSize: 20, fontWeight: 700, color: K.t0, lineHeight: 1.2 }}>
              {displayName || profile?.email?.split('@')[0] || 'Your Profile'}
            </div>
            <div style={{ fontSize: 11, color: K.t3, marginTop: 2 }}>{profile?.email}</div>
            <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
              <span style={{ fontFamily: K.fSans, fontSize: 10, fontWeight: 600, letterSpacing: 0.3, color: roleBadge.col, background: `${roleBadge.col}18`, border: `1px solid ${roleBadge.col}44`, borderRadius: 6, padding: '2px 8px' }}>{roleBadge.label}</span>
              {shift && <span style={{ fontFamily: K.fSans, fontSize: 10, fontWeight: 600, letterSpacing: 0.3, color: K.t2, background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 6, padding: '2px 8px' }}>{shift} Shift</span>}
            </div>
          </div>
        </div>

        {/* Form fields */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <div style={{ fontFamily: K.fSans, fontSize: 11, fontWeight: 600, color: K.t3, letterSpacing: 0.3, marginBottom: 4 }}>Display name</div>
            <input type="text" placeholder="First Last" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={inputStyle()} />
          </div>

          <div>
            <div style={{ fontFamily: K.fSans, fontSize: 11, fontWeight: 600, color: K.t3, letterSpacing: 0.3, marginBottom: 4 }}>Title / Position</div>
            <select value={title} onChange={(e) => setTitle(e.target.value)} style={{ ...inputStyle(), appearance: 'auto' }}>
              <option value="">Select title...</option>
              {TITLES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>

          <div>
            <div style={{ fontFamily: K.fSans, fontSize: 11, fontWeight: 600, color: K.t3, letterSpacing: 0.3, marginBottom: 4 }}>Shift</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {SHIFTS.map((s) => (
                <button
                  key={s}
                  onClick={() => setShift(shift === s ? '' : s)}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: 8, cursor: 'pointer',
                    background: shift === s ? `${K.red}22` : K.bg2,
                    border: `1px solid ${shift === s ? K.red : K.bdr}`,
                    color: shift === s ? K.red : K.t1,
                    fontFamily: K.fSans, fontSize: 14, fontWeight: 600,
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div style={{ fontFamily: K.fSans, fontSize: 11, fontWeight: 600, color: K.t3, letterSpacing: 0.3, marginBottom: 4 }}>Phone</div>
            <input type="tel" placeholder="(555) 555-5555" value={phone} onChange={(e) => setPhone(e.target.value)} style={inputStyle()} />
          </div>

          <div>
            <div style={{ fontFamily: K.fSans, fontSize: 11, fontWeight: 600, color: K.t3, letterSpacing: 0.3, marginBottom: 4 }}>Certifications</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {CERTIFICATIONS.map((cert) => {
                const active = certs.includes(cert);
                return (
                  <button
                    key={cert}
                    onClick={() => toggleCert(cert)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 4,
                      padding: '6px 10px', borderRadius: 8, cursor: 'pointer',
                      background: active ? `${K.ok}22` : K.bg2,
                      border: `1px solid ${active ? K.ok : K.bdr}`,
                      color: active ? K.ok : K.t2,
                      fontFamily: K.fSans, fontSize: 11, fontWeight: active ? 600 : 400,
                    }}
                  >
                    {active && <Check size={13} color={K.ok} strokeWidth={2} />}{cert}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Push Notifications */}
        <div style={{ marginTop: 20, padding: '14px 16px', background: K.bg2, border: `1px solid ${K.bdr}`, borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, color: K.t0, fontWeight: 700, marginBottom: 3 }}>Push notifications</div>
              <div style={{ fontSize: 10, color: K.t3, lineHeight: 1.5 }}>
                {!push.supported ? 'Not supported on this browser. iOS users: install the app to your home screen first.'
                  : push.permission === 'denied' ? 'Blocked in browser settings — re-enable to receive alerts.'
                  : push.subscribed ? 'On — you\'ll get alerts for new announcements.'
                  : 'Get pushed when new announcements post, even when the app is closed.'}
              </div>
            </div>
            {push.supported && push.permission !== 'denied' && (
              <button
                onClick={push.subscribed ? push.unsubscribe : push.subscribe}
                disabled={push.loading}
                style={{
                  background: push.subscribed ? K.ok : K.bg3,
                  border: `1px solid ${push.subscribed ? K.ok : K.bdr2}`,
                  color: push.subscribed ? '#f5f5f5' : K.t1,
                  padding: '8px 14px', borderRadius: 8,
                  cursor: push.loading ? 'default' : 'pointer',
                  fontFamily: K.fSans, fontSize: 12, fontWeight: 600,
                  letterSpacing: 0.3, whiteSpace: 'nowrap',
                  opacity: push.loading ? 0.6 : 1,
                }}
              >
                {push.loading ? '…' : push.subscribed ? 'On' : 'Enable'}
              </button>
            )}
          </div>
        </div>

        {/* Actions */}
        <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Btn col={K.red} block onClick={handleSave}>
            {saving ? 'Saving...' : saved ? '✓ Saved' : 'Save Profile'}
          </Btn>

          <button
            onClick={async () => {
              // Clear user-tied / potentially-sensitive local state so the next
              // user on a shared device doesn't inherit it. Device-level
              // calculator prefs (inputs, theme) are intentionally preserved.
              try {
                localStorage.removeItem('hydro_quiz_score');
                localStorage.removeItem('hydro_incident_draft');
              } catch { /* ignore */ }
              try { await signOut(auth); } catch (e) { console.error('[ProfileModal] sign-out error:', e); }
              onClose();
            }}
            style={{
              width: '100%', background: 'none', border: `1px solid ${K.bdr}`,
              borderRadius: 8, padding: '12px', cursor: 'pointer',
              color: K.t2, fontSize: 13, fontWeight: 600, fontFamily: K.fSans, letterSpacing: 0.2,
            }}
          >
            Sign Out
          </button>

          <button onClick={onClose} style={{ width: '100%', background: 'none', border: 'none', color: K.t3, cursor: 'pointer', fontSize: 12, fontFamily: K.fSans, padding: '8px' }}>
            Close
          </button>
        </div>

        {/* Azure SSO placeholder — future integration point */}
        {/* To enable Microsoft SSO:
            1. Register app in Azure AD (portal.azure.com > App registrations)
            2. Enable Microsoft provider in Firebase Console > Auth > Sign-in method
            3. Add OAuthProvider('microsoft.com') with tenant ID
            4. On sign-in, populate profile fields from Microsoft Graph:
               - displayName from graph /me
               - photoUrl from graph /me/photo/$value
               - email from graph /me/mail
            5. Map Azure AD groups to roles (admin/officer/member)
        */}
      </div>
    </>
  );
}
